import CloseSvg from '@/assets/close.svg'
import { customFetch } from '@/controllers/request'
import VoiceBar from '@/assets/voice-bar.svg'
import { locationContext } from '@/contexts/locationContext'
import React, { useContext, useEffect, useRef, useState } from 'react'

// 声明WebAudioSpeechRecognizer全局类型
declare const WebAudioSpeechRecognizer: any

/**
 * 语音识别组件属性接口
 *
 * @param onRecognizedText - 识别到文字时的回调函数
 * @param visible - 是否显示语音识别界面
 * @param onClose - 关闭语音识别界面的回调函数
 */
interface VoiceRecognitionProps {
  onRecognizedText: (text: string) => void
  visible: boolean
  onClose: () => void
}

/**
 * 语音识别组件
 *
 * 这是一个基于WebAudioSpeechRecognizer的语音识别组件，支持实时语音转文字功能。
 * 集成了腾讯云语音识别服务，提供连接、识别、取消等完整的语音识别流程。
 */
const SpeechRecognizer: React.FC<VoiceRecognitionProps> = ({ onRecognizedText, visible, onClose }) => {
  // 应用配置上下文，用于获取API地址
  const { appConfig } = useContext(locationContext)

  // 语音识别状态：空闲、连接中、识别中
  const [status, setStatus] = useState<'idle' | 'connecting' | 'recognizing'>('idle')

  // 当前识别出的文字内容
  const [recognizedText, setRecognizedText] = useState<string>('')

  // 语音识别器实例引用
  const recognizerRef = useRef<any>(null)

  // 是否可以停止识别的标志
  const isCanStopRef = useRef<boolean>(false)

  // 临时token引用
  const tokenRef = useRef('')

  // 从本地存储获取用户token
  const token = localStorage.getItem('lenovo-token')

  /**
   * 开始语音识别
   *
   * 获取临时token并初始化语音识别器，配置识别参数和事件回调
   */
  const startRecognition = async () => {
    // 重置识别文字和状态
    setRecognizedText('')
    setStatus('connecting')

    try {
      // 获取临时token用于语音识别服务
      const response = await customFetch(`${appConfig.baiyingFilesUrl}/chatbot-server/aiAudio/getTempToken`, {
        headers: {
          'content-type': 'application/json',
          token
        }
      })
      const res = await response.json()

      if (res.code === 200) {
        // 创建语音识别器实例，配置识别参数
        recognizerRef.current = new WebAudioSpeechRecognizer({
          secretid: res.data.tmpSecretId, // 临时密钥ID
          secretkey: res.data.tmpSecretKey, // 临时密钥
          token: res.data.token, // 临时token
          appid: '1326716587', // 应用ID
          engine_model_type: '16k_zh', // 中文16k模型
          needvad: 1, // 启用语音活动检测
          filter_dirty: 1, // 过滤脏话
          vad_silence_time: 1000, // 静音检测时间(ms)
          filter_punc: 1, // 过滤标点符号
          max_speak_time: 5000, // 最大说话时间(ms)
          noise_threshold: 0.3 // 噪音阈值
        })

        // 识别开始事件回调
        recognizerRef.current.OnRecognitionStart = (res: any) => {
          setStatus('recognizing')
        }

        // 一句话开始事件回调
        recognizerRef.current.OnSentenceBegin = (res: any) => {
          isCanStopRef.current = true
        }

        // 识别结果变化事件回调
        recognizerRef.current.OnRecognitionResultChange = (res: any) => {
          if (isCanStopRef.current) {
            setRecognizedText(res.result.voice_text_str)
          }
        }

        // 一句话结束事件回调
        recognizerRef.current.OnSentenceEnd = (res: any) => {
          if (isCanStopRef.current) {
            onRecognizedText(res.result.voice_text_str)
            setRecognizedText('')
            stopRecognition()
          }
        }

        // 识别完成事件回调
        recognizerRef.current.OnRecognitionComplete = (res: any) => {
          stopRecognition()
        }

        // 识别错误事件回调
        recognizerRef.current.OnError = (res: any) => {
          setStatus('idle')
        }

        // 启动语音识别
        recognizerRef.current.start()
      }
    } catch (error) {
      console.error('Failed to start recognition:', error)
      setStatus('idle')
    }
  }

  /**
   * 停止语音识别
   *
   * 停止当前识别过程，清理资源并关闭界面
   */
  const stopRecognition = (): void => {
    setStatus('idle')
    onClose()

    // 停止识别器并销毁音频流
    recognizerRef?.current?.stop()
    recognizerRef?.current?.destroyStream()
    isCanStopRef.current = false
  }

  /**
   * 监听visible变化，自动开始语音识别
   * 当组件变为可见时，自动启动语音识别流程
   */
  useEffect(() => {
    async function handleStart() {
      if (!tokenRef.current) {
        // await fetchToken()
      }
      startRecognition()
    }

    if (visible) {
      handleStart()
    }
  }, [visible])

  /**
   * 获取认证token
   *
   * 从服务器获取用于语音识别服务的认证token
   * 注意：当前代码中此函数被注释掉，实际使用的是getTempToken接口
   */
  const fetchToken = async () => {
    try {
      const response = await customFetch(`${appConfig.baiyingFilesUrl}/gateway/token/auth`, {
        method: 'POST',
        headers: {
          'content-type': 'application/json'
        },
        body: JSON.stringify({ appId: 'f546786038604a83baab2e133626e32b', secret: '4792386812184b1c9253f6bc51797b66' })
      })
      const res = await response.json()
      if (res.code === 200) {
        tokenRef.current = res.data.token
      }
    } catch (error) {
      console.error('Failed to fetch token:', error)
    }
  }

  /**
   * 组件卸载时的清理工作
   * 确保语音识别器被正确停止和销毁
   */
  useEffect(() => {
    return () => {
      if (recognizerRef.current && isCanStopRef.current) {
        recognizerRef.current.stop()
        recognizerRef.current.destroyStream()
      }
    }
  }, [])

  // 如果不可见，不渲染任何内容
  if (!visible) return null

  return (
    <div className="speechRecognizer-box">
      {/* 语音波形动画图标 */}
      <img src={VoiceBar} alt="" className="bar" />

      {/* 状态显示区域 */}
      <div className="text">
        {status === 'connecting' && <div>建立连接中...</div>}
        {status === 'recognizing' && <div>{recognizedText ? recognizedText : '识别中...'}</div>}
      </div>

      {/* 识别状态下的操作按钮 */}
      {status === 'recognizing' && (
        <>
          {/* 取消按钮 */}
          <div className="cancel" onClick={stopRecognition}>
            点击取消
          </div>
          {/* 关闭按钮 */}
          <img src={CloseSvg} alt="" className="close" onClick={stopRecognition} />
        </>
      )}
    </div>
  )
}

export default SpeechRecognizer
